/**
 * PlatformSettings — Phase 5 Slice 5.4 (`ai-docs/llm-platform-settings-
 * page-lexportal-handoff.md`).
 *
 * The PLATFORM_ADMIN-only destination for every `/platform/settings`
 * link the CreateOrgWizard banner emits (Slice 5.3, commit `d2f8de91`)
 * — three sections, top-down in order of operator likelihood:
 *
 *   1. Default LLM preference — provider + authMode editor (PATCH
 *      `/api/platform/settings`). This is a *preference* that new orgs
 *      inherit at provision time, NOT a credential. The canonical
 *      mandatory-credential gate lives at the BullMQ pipeline step
 *      (`init-org-config.assertOrgHasUsableCredential`).
 *
 *   2. Platform credential status — read-only ok/missing/expired pips
 *      for the Phase 5 KV secrets, plus a copy-pasteable `az` hint when
 *      any are missing. Status-only — secret material never leaves
 *      lexserver (Slice 5.1 contract). Refetch on Save in the Default
 *      LLM card so a freshly-staged credential reflects immediately.
 *
 *   3. Reseed existing orgs — Path-2 placeholder per the handoff
 *      (§Open design Q): disabled button + cross-link to the operator
 *      runbook. The actual BullMQ reseed pipeline (Path 1) is deferred
 *      to Phase 7.4 alongside the prod credential-sync work.
 *
 * Routed at `/platform/settings` (App.tsx) and surfaced in the platform
 * sidebar between Health and Runtimes.
 */

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, Loader2, Save, XCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageShell, PageHeader } from "@/components/layout/PageShell";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { LlmProfilesEditor } from "@/components/platform/LlmProfilesEditor";
import { SeedCredentialsEditor } from "@/components/platform/SeedCredentialsEditor";
import { SearchSeedCredentialsEditor } from "@/components/platform/SearchSeedCredentialsEditor";

// Provider list = the four LLM providers the wizard's credential
// banner copy enumerates (no shared `ORG_LLM_PROVIDERS` constant
// exists — the wizard hardcodes its TIERS list, not providers).
// The backend Zod schema (`PlatformSettingsPatchSchema` at lexserver/
// src/routes/platform.ts:153) accepts any non-empty string for
// `provider`, so this list is *convention*, not validation. If a
// non-listed provider ever appears server-side it renders as a
// free-form fallback option (see the `KNOWN_PROVIDERS.some(...)` guard
// in the dropdown render below).
const KNOWN_PROVIDERS = [
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
  { value: "google", label: "Google" },
  { value: "xai", label: "xAI" },
] as const;

type AuthMode = "oauth" | "api-key";
type OauthStatus = "ok" | "missing" | "expired";
type KeyStatus = "ok" | "missing";

interface SaveToast {
  ok: boolean;
  text: string;
}

/** Render a colored status pip + label. Mirrors the CreateOrgWizard banner palette. */
function StatusPip({
  status,
  okLabel,
  missingLabel,
  expiredLabel,
}: {
  status: OauthStatus | KeyStatus;
  okLabel: string;
  missingLabel: string;
  expiredLabel?: string;
}) {
  if (status === "ok") {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        <span className="font-medium text-emerald-700 dark:text-emerald-400">
          {okLabel}
        </span>
      </span>
    );
  }
  if (status === "expired") {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <span className="font-medium text-amber-700 dark:text-amber-400">
          {expiredLabel ?? "Expired"}
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <XCircle className="h-4 w-4 text-muted" />
      <span className="font-medium text-muted">{missingLabel}</span>
    </span>
  );
}

export function PlatformSettings() {
  const api = usePlatformApi();

  // ── State: form (Default LLM) ────────────────────────────────────
  const [provider, setProvider] = useState<string>("openai");
  const [authMode, setAuthMode] = useState<AuthMode>("oauth");
  const [updatedAt, setUpdatedAt] = useState<string | undefined>();
  const [updatedBy, setUpdatedBy] = useState<string | undefined>();
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<SaveToast | null>(null);

  // ── State: credential-status panel ───────────────────────────────
  const [oauthStatus, setOauthStatus] = useState<OauthStatus>("missing");
  const [keyStatus, setKeyStatus] = useState<KeyStatus>("missing");
  const [credLoading, setCredLoading] = useState(true);
  const [credError, setCredError] = useState<string | null>(null);

  // ── Initial load: settings + credential status in parallel ──────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [settingsRes, credRes] = await Promise.all([
          api.getPlatformSettings(),
          api.getPlatformCredentialStatus().catch((err) => {
            // Credential status is best-effort — if the probe fails the
            // page still renders the Default LLM editor. Surface a
            // muted error inline instead of breaking the whole page.
            if (!cancelled) {
              setCredError(
                err instanceof Error ? err.message : "Status probe failed",
              );
            }
            return null;
          }),
        ]);
        if (cancelled) return;
        setProvider(settingsRes.data.defaultLlm.provider);
        setAuthMode(settingsRes.data.defaultLlm.authMode);
        setUpdatedAt(settingsRes.data.updatedAt);
        setUpdatedBy(settingsRes.data.updatedBy);
        if (credRes) {
          setOauthStatus(credRes.data.openaiOauth);
          setKeyStatus(credRes.data.openrouter);
        }
      } catch (err) {
        if (!cancelled) {
          setToast({
            ok: false,
            text:
              err instanceof Error
                ? `Load failed: ${err.message}`
                : "Failed to load platform settings",
          });
        }
      } finally {
        if (!cancelled) {
          setLoadingSettings(false);
          setCredLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  // ── Refetch credential status without flashing the loading state
  // (the CreateOrgWizard bug the handoff warned about — don't reset
  // to a spinner during refetch; just overwrite when the response
  // lands).
  const refetchCredStatus = useCallback(async () => {
    try {
      const res = await api.getPlatformCredentialStatus();
      setOauthStatus(res.data.openaiOauth);
      setKeyStatus(res.data.openrouter);
      setCredError(null);
    } catch (err) {
      setCredError(err instanceof Error ? err.message : "Status probe failed");
    }
  }, [api]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setToast(null);
    try {
      const res = await api.updatePlatformSettings({
        defaultLlm: { provider, authMode },
      });
      setUpdatedAt(res.data.updatedAt);
      setUpdatedBy(res.data.updatedBy);
      setToast({
        ok: true,
        text: "Default LLM saved. New orgs will inherit this preference on next provision.",
      });
      // Operator likely just rotated/staged a credential — refresh
      // status pips so a stale "missing" doesn't linger after Save.
      void refetchCredStatus();
      // Auto-dismiss the success toast; errors stick until next Save.
      setTimeout(() => {
        setToast((t) => (t?.ok ? null : t));
      }, 5000);
    } catch (err) {
      setToast({
        ok: false,
        text: err instanceof Error ? err.message : "Failed to save",
      });
    } finally {
      setSaving(false);
    }
  }, [api, provider, authMode, refetchCredStatus]);

  // True if any credential needs staging — drives the `az` hint visibility.
  const credsNeedStaging =
    oauthStatus === "missing" ||
    oauthStatus === "expired" ||
    keyStatus === "missing";

  return (
    <PageShell data-testid="platform-settings-page">
      <PageHeader
        title="Platform Settings"
        subtitle="PLATFORM_ADMIN · defaults inherited by every new organization at provision time"
      />

      {/* ── P7 of llm-resolver-unification-impl-plan.md: the bundled
          DefaultLlmChoiceEditor (Slice 1.E.2) is replaced by two
          independent editors. Profile definitions and seed credentials
          rotate on different cadences — splitting them removes the
          all-or-nothing coupling of the old bundle. ─────────────────── */}
      <LlmProfilesEditor />
      <SeedCredentialsEditor />

      {/* S5b — the SEARCH fallback plane. Adjacent to the LLM seed credentials
          above and deliberately a separate card: `searchSeedCredentials` is a
          sibling store, never merged. */}
      <SearchSeedCredentialsEditor />

      {/* ── Section 1: Default LLM preference ────────────────────── */}
      <Card data-testid="platform-settings-default-llm">
        <CardHeader>
          <CardTitle className="text-lg">Default LLM provider</CardTitle>
          <CardDescription>
            Preference only — the canonical mandatory-credential gate
            lives in the org-provision pipeline. Changing this here
            updates the seeded preference; existing orgs are unaffected
            until they call the (future) <em>Reseed existing orgs</em>{" "}
            action below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingSettings ? (
            <div className="flex items-center gap-2 text-sm text-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading settings…
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <label
                  htmlFor="platform-settings-provider"
                  className="w-32 shrink-0 text-sm font-medium text-foreground"
                >
                  Provider
                </label>
                <select
                  id="platform-settings-provider"
                  data-testid="platform-settings-provider"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  className="rounded-md border border-border bg-panel px-3 py-1.5 text-sm outline-none focus:border-primary"
                >
                  {KNOWN_PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                  {/* If the persisted provider is outside the known list
                      (e.g. a future provider we haven't added to KNOWN_
                      PROVIDERS yet), render it as a fallback option so
                      the dropdown reflects the actual server state
                      instead of silently re-defaulting to OpenAI. */}
                  {!KNOWN_PROVIDERS.some((p) => p.value === provider) && (
                    <option value={provider}>{provider}</option>
                  )}
                </select>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <span className="w-32 shrink-0 pt-1 text-sm font-medium text-foreground">
                  Auth mode
                </span>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="platform-settings-authmode"
                      data-testid="platform-settings-authmode-oauth"
                      value="oauth"
                      checked={authMode === "oauth"}
                      onChange={() => setAuthMode("oauth")}
                      className="accent-primary"
                    />
                    <span>OAuth</span>
                    <span className="text-xs text-muted">(recommended)</span>
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="platform-settings-authmode"
                      data-testid="platform-settings-authmode-apikey"
                      value="api-key"
                      checked={authMode === "api-key"}
                      onChange={() => setAuthMode("api-key")}
                      className="accent-primary"
                    />
                    <span>API key</span>
                  </label>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                <div className="text-xs text-muted" data-testid="platform-settings-meta">
                  {updatedAt ? (
                    <>
                      Last updated{" "}
                      <span className="font-mono">
                        {new Date(updatedAt).toLocaleString()}
                      </span>
                      {updatedBy ? (
                        <>
                          {" "}
                          by <span className="font-mono">{updatedBy}</span>
                        </>
                      ) : null}
                    </>
                  ) : (
                    <span className="italic">
                      Never updated — page is showing the seeded default
                      (openai / oauth).
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {toast && (
                    <span
                      className={
                        toast.ok
                          ? "text-xs text-emerald-600 dark:text-emerald-400"
                          : "text-xs text-critical"
                      }
                      data-testid="platform-settings-toast"
                    >
                      {toast.text}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || loadingSettings}
                    data-testid="platform-settings-save"
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Save
                      className={`h-4 w-4 ${saving ? "animate-pulse" : ""}`}
                    />
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Section 2: Platform credential status (read-only) ────── */}
      <Card data-testid="platform-settings-credential-status">
        <CardHeader>
          <CardTitle className="text-lg">Platform credential status</CardTitle>
          <CardDescription>
            Probes the Phase 5 KV secrets without ever returning secret
            material. Use the <code className="rounded bg-panel-2 px-1 py-0.5 font-mono text-[11px]">az keyvault secret set</code>{" "}
            hint below to stage anything reported missing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {credLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Probing KV…
            </div>
          ) : credError ? (
            <div
              className="rounded-md border border-critical/30 bg-critical/10 p-3 text-sm text-critical"
              data-testid="platform-settings-cred-error"
            >
              Credential probe failed: {credError}
            </div>
          ) : (
            <>
              <div
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-panel-2 px-3 py-2"
                data-testid="platform-settings-cred-oauth"
              >
                <span className="text-sm font-medium text-foreground">
                  OpenAI OAuth profile
                </span>
                <StatusPip
                  status={oauthStatus}
                  okLabel="Staged"
                  missingLabel="Not staged"
                  expiredLabel="Expired — refresh queued"
                />
              </div>
              <div
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-panel-2 px-3 py-2"
                data-testid="platform-settings-cred-openrouter"
              >
                <span className="text-sm font-medium text-foreground">
                  OpenRouter API key
                </span>
                <StatusPip
                  status={keyStatus}
                  okLabel="Staged"
                  missingLabel="Not staged"
                />
              </div>

              {credsNeedStaging && (
                <div
                  className="space-y-2 rounded-md border border-amber-300/40 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-300"
                  data-testid="platform-settings-cred-stage-hint"
                >
                  <div className="flex items-start gap-2">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="space-y-1.5">
                      <p className="font-medium">Stage with the Azure CLI:</p>
                      {(oauthStatus === "missing" ||
                        oauthStatus === "expired") && (
                        <pre className="overflow-x-auto rounded bg-panel-2 p-2 font-mono text-[11px] text-foreground">
{`az keyvault secret set \\
  --vault-name kv-lexiun-br \\
  --name platform-default-openai-oauth-profile \\
  --file <profile.json>`}
                        </pre>
                      )}
                      {keyStatus === "missing" && (
                        <pre className="overflow-x-auto rounded bg-panel-2 p-2 font-mono text-[11px] text-foreground">
{`az keyvault secret set \\
  --vault-name kv-lexiun-br \\
  --name platform-default-openrouter-key \\
  --value <sk-or-…>`}
                        </pre>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Section 3: Reseed existing orgs (Path 2 placeholder) ── */}
      <Card data-testid="platform-settings-reseed">
        <CardHeader>
          <CardTitle className="text-lg">Reseed existing orgs</CardTitle>
          <CardDescription>
            Re-encrypts the platform defaults into every ACTIVE org's
            <code className="ml-1 rounded bg-panel-2 px-1 py-0.5 font-mono text-[11px]">runtimeConfig.llm.&#123;authProfilesJson, providersJson&#125;</code>.
            Use this after rotating a platform-default credential.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2 rounded-md border border-border bg-panel-2 p-3 text-xs text-muted">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium text-foreground">
                Coming in Phase 7.4 — Org credential sync
              </p>
              <p className="mt-1">
                The in-cluster BullMQ reseed pipeline lands alongside
                the prod credential-sync work (
                <code className="rounded bg-panel px-1 py-0.5 font-mono text-[11px]">
                  scripts/sync-org-credentials-to-prod.ts
                </code>
                , Phase 7.4 of{" "}
                <code className="rounded bg-panel px-1 py-0.5 font-mono text-[11px]">
                  ai-docs/llm-provider-oauth-implementation-plan.md
                </code>
                ). If an immediate reseed is needed today, an operator
                can run{" "}
                <code className="rounded bg-panel px-1 py-0.5 font-mono text-[11px]">
                  lexserver/scripts/audit-org-llm-credentials.ts --apply --seed-default
                </code>{" "}
                from a shell (the script is self-documenting via{" "}
                <code className="rounded bg-panel px-1 py-0.5 font-mono text-[11px]">--help</code>
                ).
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled
            data-testid="platform-settings-reseed-btn"
            title="Coming in Phase 7.4 — use lexserver/scripts/audit-org-llm-credentials.ts --apply --seed-default from a shell for immediate-rotation needs"
            className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-md border border-border bg-panel-2 px-4 py-2 text-sm font-medium text-muted opacity-60"
          >
            Run reseed (disabled — Phase 7.4)
          </button>
        </CardContent>
      </Card>
    </PageShell>
  );
}
